import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {applicativeValidation, lift} from 'Shared/utilities/ToValidation';
import {sequenceT} from 'fp-ts/lib/Apply';
import {Either, getOrElse, left, map as mapEither, mapLeft, right} from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';
import {ValidUserName} from 'Users/models/UserName';

const userNamesMustMatch = (userNameTuple: [string, string]): Either<string, [string, string]> => {
  const [userName, confirmationUserName] = userNameTuple;
  return userName === confirmationUserName ? right([userName, confirmationUserName]) : left('Usernames must match');
};

const userNameMustNotBeEmpty = (userNameTuple: [string, string]): Either<string, [string, string]> => {
  const [userName, confirmationUserName] = userNameTuple;
  const validatedUserName = ValidUserName.create(userName);

  return validatedUserName instanceof ValidUserName
    ? right([userName, confirmationUserName])
    : left('Username cannot be blank.');
};

const userNamesMustMatchV = lift(userNamesMustMatch);
const userNameMustNotBeEmptyV = lift(userNameMustNotBeEmpty);

const validateNewUserName = (username: string, confirmation: string) =>
  pipe(
    sequenceT(applicativeValidation)(
      userNameMustNotBeEmptyV([username, confirmation]),
      userNamesMustMatchV([username, confirmation])
    ),
    mapEither(() => [username, confirmation])
  );

export class UnvalidatedNewUserName {
  public readonly key = 'UnvalidatedNewUserName';

  constructor(public readonly username: string, public readonly confirmation: string) {}
}

export class InvalidNewUserName {
  public static create(username: string, confirmation: string, errors: string[]) {
    return new InvalidNewUserName(username, confirmation, errors);
  }
  public readonly key = 'InvalidNewUserName';

  private constructor(
    public readonly username: string,
    public readonly confirmation: string,
    public readonly errors: string[]
  ) {}
}

export class ValidNewUserName {
  public static create(username: string, confirmation: string): NewUserName {
    const usernameEither = pipe(
      validateNewUserName(username, confirmation),
      mapLeft(errors => InvalidNewUserName.create(username, confirmation, errors)),
      mapEither(([validUserName, validConfirmation]) => new ValidNewUserName(validUserName, validConfirmation))
    );

    return pipe(
      usernameEither,
      getOrElse(invalidUsername => invalidUsername as NewUserName)
    );
  }

  public readonly key = 'ValidNewUserName';
  private constructor(public readonly username: string, public readonly confirmation: string) {}
}

export type NewUserName = ValidNewUserName | InvalidNewUserName | UnvalidatedNewUserName;

export type NewUserNameOptions<TReturn> = ToMatchOptions<NewUserName, 'key', TReturn>;

export const matchNewUserName = <TReturn>(username: NewUserName, options: NewUserNameOptions<TReturn>): TReturn => {
  switch (username.key) {
    case 'InvalidNewUserName':
      return options.InvalidNewUserName(username);
    case 'UnvalidatedNewUserName':
      return options.UnvalidatedNewUserName(username);
    case 'ValidNewUserName':
      return options.ValidNewUserName(username);
  }
};
